import { EVENTS } from 'util.events'

/**
 * Keeps the quantity tier radios and the quantity input of the buy box in sync:
 * - selecting a tier sets the quantity
 * - changing the quantity (input, +/- buttons, sticky add to cart) selects the matching tier
 */
class ProductBuyBox extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.addEventListener(
      'change',
      (event) => {
        if (event.target.matches('[data-buy-box-tier]')) {
          this.setQuantity(event.target.value)
        } else if (event.target.matches('.wine-pdp-qty-input')) {
          this.syncTier()
        }
      },
      { signal }
    )

    this.addEventListener(
      'input',
      (event) => {
        if (event.target.matches('.wine-pdp-qty-input')) this.syncTier()
      },
      { signal }
    )

    // The +/- buttons update the value in a document-level listener (block.product-buy-buttons),
    // which runs after this one, so sync on the next tick
    this.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.wine-pdp-qty-plus, .wine-pdp-qty-minus')) {
          setTimeout(() => this.syncTier())
        }
      },
      { signal }
    )

    document.addEventListener(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      (event) => this.handleVariantChange(event),
      { signal }
    )

    this.syncTier()
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  get quantityInput() {
    return this.querySelector('.wine-pdp-qty-input')
  }

  get tierInputs() {
    return Array.from(this.querySelectorAll('[data-buy-box-tier]'))
  }

  setQuantity(value) {
    const input = this.quantityInput
    if (!input) return

    input.value = value
    // "change" also updates the sticky add to cart quantity
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  syncTier() {
    const input = this.quantityInput
    if (!input) return

    const quantity = parseInt(input.value, 10) || 1
    const tiers = this.tierInputs.sort((a, b) => Number(a.value) - Number(b.value))

    // Highest tier whose minimum quantity is reached, fallback: first tier
    const match = tiers.filter((tier) => quantity >= Number(tier.value)).pop() || tiers[0]

    if (match) match.checked = true
  }

  handleVariantChange({ detail }) {
    const { html, loading } = detail
    if (loading || !html) return

    const source = html.querySelector(
      `product-buy-box[data-section-id="${this.dataset.sectionId}"] [data-buy-box-tiers]`
    )
    const destination = this.querySelector('[data-buy-box-tiers]')

    if (source && destination) {
      destination.innerHTML = source.innerHTML
      this.syncTier()
    }
  }
}

if (!customElements.get('product-buy-box')) {
  customElements.define('product-buy-box', ProductBuyBox)
}
